// v2 axios client — self-contained instance for the new UI path.
//
// Ports the proven request/refresh mechanics from the legacy
// src/components/axiosApi/axiosApi.js, but:
//   - points at API_BASE (the new /api namespace) only,
//   - reads/writes the isolated ss-v2-* token storage,
//   - refreshes via POST /api/auth/refreshtoken.
//
// It shares NO state with the legacy axios instance.
import axios from 'axios';

import { API_BASE } from './config';
import {
  getAccessToken,
  getRefreshToken,
  getUserId,
  saveSession,
  clearSession,
} from '../storage/tokens';

const BEARER = 'Bearer';
// Marks the refresh request so its own 401 never triggers another refresh.
const NO_RETRY_PARAM = 'do_not_call_twice';

const client = axios.create({ baseURL: API_BASE });

// Skip auth/retry handling for the refresh call itself (avoids infinite loops).
const isNoRetry = (url = '') => {
  const query = url.includes('?') ? url.split('?')[1] : '';
  return new URLSearchParams(query).get(NO_RETRY_PARAM) === 'true';
};

// A 401 from these endpoints means bad credentials, not an expired token —
// refreshing and retrying would just resend the same rejected credentials.
const AUTH_ENDPOINTS = ['/auth/login', '/auth/register'];
const isAuthEndpoint = (url = '') => AUTH_ENDPOINTS.some((path) => url.startsWith(path));

// --- request: attach bearer token ---
client.interceptors.request.use((request) => {
  if (isNoRetry(request.url)) return request;

  const jwt = getAccessToken();
  if (jwt) {
    request.headers.Authorization = `${BEARER} ${jwt}`;
  }
  return request;
});

// --- response: refresh once on 401, then retry ---
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest || isNoRetry(originalRequest.url) || isAuthEndpoint(originalRequest.url)) {
      return Promise.reject(error);
    }

    const isUnauthorized = error.response?.status === 401;
    if (isUnauthorized && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshed = await requestRefresh();
      if (!refreshed) {
        clearSession();
        return Promise.reject(error);
      }

      saveSession(refreshed);
      const jwt = getAccessToken();
      if (jwt) {
        originalRequest.headers.Authorization = `${BEARER} ${jwt}`;
      }
      return client(originalRequest); // retry once with the new token
    }

    return Promise.reject(error);
  }
);

// Exchanges the refresh token for a fresh session. Uses a bare axios call
// (not `client`) so it bypasses interceptors entirely.
const requestRefresh = async () => {
  const refreshToken = getRefreshToken();
  const userID = getUserId();

  if (!refreshToken || !userID) {
    return null;
  }

  const url = `${API_BASE}/auth/refreshtoken?${NO_RETRY_PARAM}=true`;

  try {
    const response = await axios.post(url, { refreshToken, userID });
    return response.data; // expected: { tokens, user }
  } catch (err) {
    console.error('v2 client.requestRefresh failed', err);
    return null;
  }
};

// --- proactive silent refresh ---
// A page like the print designer can sit open for 15+ minutes of pure
// editing with no requests in flight, so the access token can expire before
// the reactive 401-then-refresh path in the interceptor above ever runs —
// and by the time it does, the refresh token itself may have also expired.
// Instead, schedule a silent refresh ahead of the access token's own
// expiry so a long-idle tab always has a live token when the user finally
// acts. AuthContext starts/stops this alongside its own session lifecycle.
const REFRESH_BUFFER_MS = 30_000; // refresh this long before actual expiry
const FALLBACK_INTERVAL_MS = 4 * 60_000; // used if the token isn't a decodable JWT

let refreshTimer = null;

const decodeJwtExpMs = (token) => {
  try {
    const [, payload] = token.split('.');
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const stopAutoRefresh = () => {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
};

export const startAutoRefresh = () => {
  stopAutoRefresh();

  const jwt = getAccessToken();
  if (!jwt) return;

  const expMs = decodeJwtExpMs(jwt);
  const delay = expMs != null ? Math.max(expMs - Date.now() - REFRESH_BUFFER_MS, 0) : FALLBACK_INTERVAL_MS;

  refreshTimer = setTimeout(async () => {
    const refreshed = await requestRefresh();
    if (refreshed) {
      saveSession(refreshed);
      startAutoRefresh(); // reschedule off the new token's own expiry
    }
    // Refresh failed (session genuinely dead) — leave it. The next real
    // request's reactive 401 handling above will surface that.
  }, delay);
};

export default client;
